import Cookies from 'js-cookie';

import { assertCustomerTransportAvailable } from '../account/impersonation';
import { RAILS_URL, XSRF_COOKIE_KEY } from '../globals';

interface fetchWithRetryOptions extends RequestInit {
  retries?: number;
  retryDelay?: number; // in milliseconds
}

/**
 * A wrapper around `fetch` with optional retries.
 * Retries up to `options.retries` times (defaults to `5` if not specified)
 * with a delay of `options.retryDelay` (defaults to `500ms` if not specified)
 */
const fetchWithRetry = async (url: string, options: fetchWithRetryOptions): Promise<Response> => {
  const retries = options.retries || 0;
  const retryDelay = options.retryDelay || 500;

  try {
    return await fetch(url, options);
  } catch (err) {
    if (retries < 1) throw err;

    // sleep the specified amount of time, then recursively retry
    await new Promise((r) => setTimeout(r, retryDelay));
    return await fetchWithRetry(url, { ...options, retries: retries - 1 });
  }
};

/**
 * Merges caller-supplied headers over the JSON and CSRF defaults.
 *
 * Goes through `Headers` rather than an object spread because `HeadersInit` also admits a
 * `Headers` and a `string[][]`; spreading either yields `{}` or index keys, which would drop
 * the CSRF token along with the caller's own headers.
 */
const withDefaultHeaders = (callerHeaders: HeadersInit | undefined): Headers => {
  const headers = new Headers({
    Accept: 'application/json',
    'Content-Type': 'application/json',
    'X-CSRF-Token': Cookies.get(XSRF_COOKIE_KEY) || '',
  });
  new Headers(callerHeaders).forEach((value, name) => {
    headers.set(name, value);
  });
  return headers;
};

// To do: strengthen jsonParam's type
// Do not pass skip doBFFPost as it will not actually skip making the request - instead handle it conditionally
export function doBFFPost(
  apiUrl: string,
  jsonParams: object,
  options: Omit<fetchWithRetryOptions, 'method'> = {},
): Promise<Response> {
  try {
    assertCustomerTransportAvailable(`POST ${new URL(apiUrl, RAILS_URL).pathname}`);
  } catch (error) {
    return Promise.reject(error instanceof Error ? error : new Error(String(error)));
  }
  return fetchWithRetry(apiUrl, {
    retries: 5,
    retryDelay: 500,
    credentials: 'include',
    method: 'POST',
    body: JSON.stringify(jsonParams),
    ...options,
    headers: withDefaultHeaders(options.headers),
  });
}

export function doGet(
  path: string,
  options: Omit<fetchWithRetryOptions, 'body' | 'method'> = {},
): Promise<Response> {
  return fetchWithRetry(`${RAILS_URL}${path}`, {
    retries: 5,
    retryDelay: 500,
    credentials: 'include',
    ...options,
    headers: withDefaultHeaders(options.headers),
  });
}
